import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  TablePagination
} from '@mui/material';

import { Add, Search, Edit, Delete, Close } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';

const Departments = () => {
  const { user } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ departmentName: '' });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errors, setErrors] = useState({ departmentName: false });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dialogMessage, setDialogMessage] = useState({ text: '', type: '' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.DEPARTMENTS);
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.dept_id,
        departmentName: item.dept_name
      }));
      setAllRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      setMessage({ text: 'Failed to load departments', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const rowToEdit = filteredRows.find(row => row.id === id);
    setFormData({ departmentName: rowToEdit.departmentName });
    setEditMode(true);
    setEditId(id);
    setOpen(true);
  };

  const handleSearch = () => {
    const filtered = allRows.filter(row =>
      row.departmentName && row.departmentName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRows(filtered);
  };

  const handleSave = async () => {
    setErrors({ departmentName: false });
    setDialogMessage({ text: '', type: '' });

    if (!formData.departmentName.trim()) {
      setErrors({ departmentName: true });
      setDialogMessage({ text: 'Department name is mandatory', type: 'error' });
      return;
    }

    setSaveLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const url = editMode ? `${API_ENDPOINTS.DEPARTMENTS}/${editId}` : API_ENDPOINTS.DEPARTMENTS;
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          departmentName: formData.departmentName.trim(),
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} department`);
      }

      await fetchDepartments();
      setOpen(false);
      handleClear();
      setMessage({ text: `Department ${editMode ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setDialogMessage({ text: error.message, type: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({ departmentName: '' });
    setErrors({ departmentName: false });
    setDialogMessage({ text: '', type: '' });
    setEditMode(false);
    setEditId(null);
  };

  const handleDeleteClick = () => {
    if (!selectedRows || selectedRows.length === 0) {
      setMessage({ text: 'Please select items to delete', type: 'warning' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSingle = (id) => {
    setDeleteRowId(id);
    setSingleDeleteConfirmOpen(true);
  };

  const handleSingleDeleteConfirm = async () => {
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(`${API_ENDPOINTS.DEPARTMENTS}/${deleteRowId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete department');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      await fetchDepartments();
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete department', type: 'error' });
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const selectedIds = selectedRows || [];
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(API_ENDPOINTS.DEPARTMENTS, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectionModel: selectedIds,
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete departments');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      setSelectedRows([]);
      await fetchDepartments();
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete departments', type: 'error' });
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Departments</Typography>

      {(loading || saveLoading || deleteLoading) && <LinearProgress sx={{ mb: 2 }} />}
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button variant="outlined" startIcon={<Search />} onClick={handleSearch}>
            Search
          </Button>
          {filteredRows.length !== allRows.length && (
            <Button variant="outlined" onClick={() => { setFilteredRows(allRows); setSearchTerm(''); }}>
              Show All
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />} 
            onClick={handleDeleteClick}
            disabled={!selectedRows || selectedRows.length === 0 || deleteLoading}
          >
            Delete ({selectedRows?.length || 0})
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditMode(false); setEditId(null); setOpen(true); }}>
            Add Department
          </Button>
        </Box>
      </Box>

      {!loading && (
        <Box sx={{ mt: 2, height: 500, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#1976d2' }}>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>
                  <input 
                    type="checkbox" 
                    checked={paginatedRows.length > 0 && paginatedRows.every(row => selectedRows.includes(row.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows([...new Set([...selectedRows, ...paginatedRows.map(row => row.id)])]);
                      } else {
                        const pageIds = paginatedRows.map(row => row.id);
                        setSelectedRows(selectedRows.filter(id => !pageIds.includes(id)));
                      }
                    }}
                  />
                </th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Department Name</th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, index) => (
                <tr 
                  key={row.id}
                  style={{ 
                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                  }}
                  onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#e3f2fd'}
                  onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white'}
                >
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedRows.includes(row.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows([...selectedRows, row.id]);
                        } else {
                          setSelectedRows(selectedRows.filter(id => id !== row.id));
                        }
                      }}
                    />
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px' }}>{row.departmentName || 'N/A'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Edit 
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => handleEdit(row.id)}
                      />
                      <Delete 
                        sx={{ cursor: 'pointer', color: 'error.main' }}
                        onClick={() => handleDeleteSingle(row.id)}
                      />
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Department' : 'Add Department'}
          <Close sx={{ cursor: 'pointer' }} onClick={() => { setOpen(false); handleClear(); }} />
        </DialogTitle>
        <DialogContent>
          {dialogMessage.text && (
            <Alert severity={dialogMessage.type} sx={{ mb: 2 }}>
              {dialogMessage.text}
            </Alert>
          )}
          <TextField
            fullWidth
            name="departmentName"
            label="Department Name"
            value={formData.departmentName}
            onChange={(e) => setFormData({ ...formData, departmentName: e.target.value })}
            error={errors.departmentName}
            helperText={errors.departmentName ? 'Department name is required' : ''}
            sx={{ mb: 2, mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} disabled={saveLoading}>Clear</Button>
          <Button onClick={() => { setOpen(false); handleClear(); }} disabled={saveLoading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedRows?.length || 0} selected department{(selectedRows?.length || 0) > 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={singleDeleteConfirmOpen} onClose={() => setSingleDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this department?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleSingleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Departments;